/**
 * Completa el flujo de reset con oobCode de Firebase.
 */
import React, { useEffect, useState } from 'react';
import { auth } from '../firebaseConfig';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { studio } from './admin/studioUi';
import { useAuth } from '../AuthContext';
import AuthShell, {
    AuthField,
    AuthNotice,
    PasswordToggle,
    validateNewPassword,
} from './AuthShell';

const goToLogin = () => {
    window.history.replaceState({}, document.title, '/');
    window.location.reload();
};

const PasswordResetHandler = () => {
    const { withBusy } = useAuth();
    const [oobCode, setOobCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [email, setEmail] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('oobCode');
        if (!code) {
            setError('El enlace no es válido. Solicita uno nuevo desde inicio de sesión.');
            setVerifying(false);
            return;
        }
        setOobCode(code);
        verifyPasswordResetCode(auth, code)
            .then((userEmail) => {
                setEmail(userEmail);
                setVerifying(false);
            })
            .catch(() => {
                setError('El enlace expiró o no es válido. Solicita uno nuevo desde inicio de sesión.');
                setVerifying(false);
            });
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateNewPassword(newPassword);
        if (validationErrors.length > 0) {
            setError(`La contraseña debe ${validationErrors.join(', ')}.`);
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Las contraseñas no coinciden.');
            return;
        }

        setError('');
        setMessage('');
        setLoading(true);
        try {
            await withBusy(async () => {
                await confirmPasswordReset(auth, oobCode, newPassword);
            }, 'Restableciendo contraseña…');
            setMessage('Contraseña restablecida. Te llevamos al inicio de sesión.');
            setTimeout(goToLogin, 2500);
        } catch {
            setError('No se pudo restablecer. El enlace puede haber expirado. Solicita uno nuevo.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthShell
            kicker="Seguridad"
            title="Restablecer contraseña"
            subtitle={email ? `Cuenta: ${email}` : 'Valida el enlace y elige una contraseña nueva.'}
        >
            {verifying ? (
                <p style={{ ...studio.subtitle, marginTop: 0 }}>Verificando enlace…</p>
            ) : null}
            <AuthNotice type="error">{error}</AuthNotice>
            <AuthNotice type="ok">{message}</AuthNotice>
            {!message && !error && !verifying ? (
                <form onSubmit={handleSubmit} autoComplete="on">
                    <AuthField label="Nueva contraseña" htmlFor="reset-password">
                        <div style={{ position: 'relative' }}>
                            <input
                                id="reset-password"
                                name="new-password"
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                maxLength={128}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                style={{ ...studio.control, paddingRight: 48 }}
                                required
                            />
                            <PasswordToggle show={showPassword} onToggle={() => setShowPassword((v) => !v)} />
                        </div>
                    </AuthField>
                    <AuthField label="Confirmar contraseña" htmlFor="reset-password-confirm">
                        <input
                            id="reset-password-confirm"
                            name="confirm-password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            maxLength={128}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            style={studio.control}
                            required
                        />
                    </AuthField>
                    <button
                        type="submit"
                        style={{ ...studio.primaryBtn, width: '100%', minHeight: 46, fontSize: '1rem', marginTop: 8 }}
                        disabled={loading}
                    >
                        {loading ? 'Guardando…' : 'Guardar contraseña'}
                    </button>
                </form>
            ) : null}
            {(message || error) ? (
                <button
                    type="button"
                    onClick={goToLogin}
                    style={{ ...studio.primaryBtn, width: '100%', minHeight: 46, fontSize: '1rem', marginTop: 12 }}
                >
                    Ir a iniciar sesión
                </button>
            ) : null}
        </AuthShell>
    );
};

export default PasswordResetHandler;
